import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, unwrap } from "@/shared/api/client";
import { invalidate, qk } from "@/shared/api/keys";
import {
  activateOfflineAccount,
  clearOfflineAccount,
  discardOfflineDraft,
  type LastKnownProjection,
  listOfflineDraftsForReview,
  type OfflineCaptureDraft,
  type OfflineDraftReview,
  type OfflineOutboxSummary,
  offlineOutboxSummary,
  offlineStorageSupported,
  queueOfflineCapture,
  readLastKnownProjection,
  replayOfflineCaptures,
  retryOfflineDraft,
  storeLastKnownProjection,
  subscribeOfflineOutbox,
} from "./outbox";

type OfflineOutboxValue = {
  accountId: string | null;
  enabled: boolean;
  supported: boolean;
  replaying: boolean;
  summary: OfflineOutboxSummary;
  lastKnownInboxCount: LastKnownProjection<{ unprocessed: number }> | null;
  enqueueCapture: (draft: OfflineCaptureDraft) => Promise<void>;
  replay: () => Promise<void>;
  listDrafts: () => Promise<OfflineDraftReview[]>;
  retryDraft: (id: string) => Promise<void>;
  discardDraft: (id: string) => Promise<void>;
  clear: () => Promise<void>;
};

const EMPTY_SUMMARY: OfflineOutboxSummary = {
  pending: 0,
  needsReview: 0,
  total: 0,
  oldestAt: null,
};

const unavailable: OfflineOutboxValue = {
  accountId: null,
  enabled: false,
  supported: false,
  replaying: false,
  summary: EMPTY_SUMMARY,
  lastKnownInboxCount: null,
  enqueueCapture: async () => {
    throw new Error("offline_capture_unavailable");
  },
  replay: async () => undefined,
  listDrafts: async () => [],
  retryDraft: async () => undefined,
  discardDraft: async () => undefined,
  clear: async () => undefined,
};

const OfflineOutboxContext = createContext<OfflineOutboxValue>(unavailable);

export function OfflineOutboxProvider({
  accountId,
  enabled,
  children,
}: {
  accountId: string | null;
  enabled: boolean;
  children: ReactNode;
}) {
  const client = useQueryClient();
  const supported = offlineStorageSupported();
  const [summary, setSummary] = useState<OfflineOutboxSummary>(EMPTY_SUMMARY);
  const [lastKnownInboxCount, setLastKnownInboxCount] = useState<LastKnownProjection<{
    unprocessed: number;
  }> | null>(null);
  const [replaying, setReplaying] = useState(false);
  const replayingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!accountId || !supported) {
      setSummary(EMPTY_SUMMARY);
      return;
    }
    setSummary(await offlineOutboxSummary(accountId));
  }, [accountId, supported]);

  const refreshLastKnown = useCallback(async () => {
    if (!accountId || !supported) {
      setLastKnownInboxCount(null);
      return;
    }
    const cachedBoundary = client.getQueryData<{
      schema_epoch: number;
      reset_token: string | null;
    }>(qk.syncCursor(accountId));
    const boundary = cachedBoundary
      ? { schemaEpoch: cachedBoundary.schema_epoch, resetToken: cachedBoundary.reset_token }
      : undefined;
    if (navigator.onLine && enabled) {
      try {
        let resolvedBoundary = boundary;
        if (!resolvedBoundary) {
          const value = await unwrap(api.GET("/api/v4/sync/cursor", {}));
          if (typeof value.schema_epoch !== "number") throw new Error("sync_boundary_missing");
          resolvedBoundary = {
            schemaEpoch: value.schema_epoch,
            resetToken: value.reset_token ?? null,
          };
        }
        const count = await unwrap(api.GET("/api/v4/memory/capture/count", {}));
        await storeLastKnownProjection(accountId, "inbox_count", resolvedBoundary, count);
        setLastKnownInboxCount(null);
        return;
      } catch {
        // A transport failure falls through to the last verified local value.
      }
    }
    setLastKnownInboxCount(
      await readLastKnownProjection<{ unprocessed: number }>(accountId, "inbox_count", boundary),
    );
  }, [accountId, client, enabled, supported]);

  const replay = useCallback(async () => {
    if (!accountId || !enabled || !supported || !navigator.onLine || replayingRef.current) return;
    replayingRef.current = true;
    setReplaying(true);
    try {
      const result = await replayOfflineCaptures(accountId);
      if (result.delivered > 0) await invalidate(client, "inbox", "memory");
      await refresh();
      await refreshLastKnown();
    } finally {
      replayingRef.current = false;
      setReplaying(false);
    }
  }, [accountId, client, enabled, refresh, refreshLastKnown, supported]);

  useEffect(() => {
    if (!accountId || !supported) {
      setSummary(EMPTY_SUMMARY);
      setLastKnownInboxCount(null);
      return;
    }
    let active = true;
    void activateOfflineAccount(accountId)
      .then(() => (active ? refresh() : undefined))
      .then(() => (active ? refreshLastKnown() : undefined))
      .then(() => (active ? replay() : undefined))
      .catch(() => {
        if (active) {
          setSummary(EMPTY_SUMMARY);
          setLastKnownInboxCount(null);
        }
      });
    const unsubscribe = subscribeOfflineOutbox(accountId, () => void refresh());
    const onOnline = () => {
      void replay();
      void refreshLastKnown();
    };
    const onOffline = () => void refreshLastKnown();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      active = false;
      unsubscribe();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [accountId, refresh, refreshLastKnown, replay, supported]);

  const enqueueCapture = useCallback(
    async (draft: OfflineCaptureDraft) => {
      if (!accountId || !enabled || !supported) throw new Error("offline_capture_unavailable");
      await queueOfflineCapture(accountId, draft);
      await refresh();
    },
    [accountId, enabled, refresh, supported],
  );

  const listDrafts = useCallback(async () => {
    if (!accountId || !supported) return [];
    return listOfflineDraftsForReview(accountId);
  }, [accountId, supported]);

  const retryDraft = useCallback(
    async (id: string) => {
      if (!accountId) return;
      await retryOfflineDraft(accountId, id);
      await refresh();
      await replay();
    },
    [accountId, refresh, replay],
  );

  const discardDraft = useCallback(
    async (id: string) => {
      if (!accountId) return;
      await discardOfflineDraft(accountId, id);
      await refresh();
    },
    [accountId, refresh],
  );

  const clear = useCallback(async () => {
    if (!accountId) return;
    await clearOfflineAccount(accountId);
    setSummary(EMPTY_SUMMARY);
    setLastKnownInboxCount(null);
  }, [accountId]);

  const value = useMemo<OfflineOutboxValue>(
    () => ({
      accountId,
      enabled,
      supported,
      replaying,
      summary,
      lastKnownInboxCount,
      enqueueCapture,
      replay,
      listDrafts,
      retryDraft,
      discardDraft,
      clear,
    }),
    [
      accountId,
      clear,
      discardDraft,
      enabled,
      enqueueCapture,
      listDrafts,
      lastKnownInboxCount,
      replay,
      replaying,
      retryDraft,
      summary,
      supported,
    ],
  );

  return <OfflineOutboxContext.Provider value={value}>{children}</OfflineOutboxContext.Provider>;
}

export function useOfflineOutbox(): OfflineOutboxValue {
  return useContext(OfflineOutboxContext);
}
